import React, { useState, useEffect } from 'react';
import './Preloader.css';

const Preloader = () => {
  const [progress, setProgress] = useState(0);
  const [loadingText, setLoadingText] = useState("INITIALIZING RESOURCE NETWORK...");
  const [fadeOut, setFadeOut] = useState(false);
  const [show, setShow] = useState(true);

  useEffect(() => {
    const duration = 1000; // 1.0 second total loading time
    const intervalTime = 20;
    const steps = duration / intervalTime;
    let currentStep = 0;

    const timer = setInterval(() => {
      currentStep++;
      const nextProgress = Math.min((currentStep / steps) * 100, 100);
      setProgress(nextProgress);

      // Dynamic text updates based on progress
      if (nextProgress < 25) {
        setLoadingText("SYNCHRONIZING MINERAL DATA...");
      } else if (nextProgress < 50) {
        setLoadingText("STREAMING HYDRAULIC RESERVES...");
      } else if (nextProgress < 75) {
        setLoadingText("MAPPING GROWTH TELEMETRY...");
      } else if (nextProgress < 95) {
        setLoadingText("DEPLOYING OPERATIONS CANVAS...");
      } else {
        setLoadingText("SYSTEMS READY.");
      }

      if (nextProgress >= 100) {
        clearInterval(timer);
        setTimeout(() => {
          setFadeOut(true);
          setTimeout(() => {
            setShow(false);
          }, 600);
        }, 100);
      }
    }, intervalTime);

    return () => clearInterval(timer);
  }, []);

  if (!show) return null;

  // Active icon stage (1: Gold, 2: Water, 3: Cash, 4: Growth)
  const getActiveStage = () => {
    if (progress < 25) return 1;
    if (progress < 50) return 2;
    if (progress < 75) return 3;
    return 4;
  };

  const stage = getActiveStage();

  return (
    <div className={`smart-preloader-wrapper ${fadeOut ? 'fade-out' : ''}`}>
      <div className="smart-preloader-content">
        
        {/* Hexagonal Logo Assembly SVG */}
        <div className="preloader-logo-area">
          <svg className="preloader-hex-logo" viewBox="0 0 240 240">
            <defs>
              {/* Gold Bars Vector */}
              <g id="gold-icon">
                <path d="M-8,-4 L8,-4 L5,-10 L-5,-10 Z" fill="#ffffff" />
                <path d="M-12,5 L4,5 L1,-1 L-9,-1 Z" fill="#ffffff" opacity="0.9" />
                <path d="M-1,5 L15,5 L12,-1 L-4,-1 Z" fill="#ffffff" opacity="0.8" />
                <polygon points="12,-12 15,-10 12,-8 9,-10" fill="#e5c158" />
              </g>
              {/* Water Droplet Vector */}
              <g id="water-icon">
                <path d="M0,-12 C0,-12 8,-2 8,3 C8,7.4 4.4,11 0,11 C-4.4,11 -8,7.4 -8,3 C-8,-2 0,-12 0,-12 Z" fill="#4ea8de" />
              </g>
              {/* Cash Stack Vector */}
              <g id="cash-icon">
                <rect x="-11" y="-7" width="22" height="14" rx="1.5" fill="#ffffff" />
                <rect x="-13" y="-5" width="22" height="14" rx="1.5" fill="#ffffff" stroke="#7a0c2e" strokeWidth="1.5" />
                <circle cx="-2" cy="2" r="3" fill="#7a0c2e" />
              </g>
              {/* Growth Chart Vector */}
              <g id="growth-icon">
                <rect x="-11" y="2" width="3" height="6" fill="#ffffff" />
                <rect x="-6" y="-1" width="3" height="9" fill="#ffffff" />
                <rect x="-1" y="-5" width="3" height="13" fill="#ffffff" />
                <rect x="4" y="-9" width="3" height="17" fill="#ffffff" />
                <path d="M-12,5 L6,-8 L10,-8 L10,-4" fill="none" stroke="#e53e3e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </g>
            </defs>

            {/* Satellite Nodes (Outer decorative hexes centered at X=120) */}
            <g className={`satellite-node ${stage >= 1 ? 'active' : ''}`} transform="translate(120, 36)">
              <polygon points="0,-16 14,-8 14,8 0,16 -14,8 -14,-8" className="sat-hex" />
            </g>
            <g className={`satellite-node ${stage >= 2 ? 'active' : ''}`} transform="translate(62, 110)">
              <polygon points="0,-16 14,-8 14,8 0,16 -14,8 -14,-8" className="sat-hex" />
            </g>
            <g className={`satellite-node ${stage >= 3 ? 'active' : ''}`} transform="translate(178, 110)">
              <polygon points="0,-16 14,-8 14,8 0,16 -14,8 -14,-8" className="sat-hex" />
            </g>
            <g className={`satellite-node ${stage >= 4 ? 'active' : ''}`} transform="translate(120, 184)">
              <polygon points="0,-16 14,-8 14,8 0,16 -14,8 -14,-8" className="sat-hex" />
            </g>

            {/* Connecting Lines */}
            <line x1="120" y1="52" x2="120" y2="70" className="connector-line" />
            <line x1="76" y1="110" x2="85" y2="110" className="connector-line" />
            <line x1="164" y1="110" x2="155" y2="110" className="connector-line" />
            <line x1="120" y1="168" x2="120" y2="150" className="connector-line" />

            {/* MAIN CENTRAL HEXAGON LOGO - Centered at (120, 110) */}
            <g className="main-center-hex" transform="translate(120, 110)">
              <polygon points="0,-40 35,-20 35,20 0,40 -35,20 -35,-20" className="center-hex-cell" />
              
              {/* Dynamic Icons centered at X=120 */}
              <g className={`icon-wrapper ${stage === 1 ? 'show' : ''}`}>
                <use href="#gold-icon" transform="scale(1.3)" />
              </g>
              <g className={`icon-wrapper ${stage === 2 ? 'show' : ''}`}>
                <use href="#water-icon" transform="translate(0, 1) scale(1.3)" />
              </g>
              <g className={`icon-wrapper ${stage === 3 ? 'show' : ''}`}>
                <use href="#cash-icon" transform="scale(1.2)" />
              </g>
              <g className={`icon-wrapper ${stage === 4 ? 'show' : ''}`}>
                <use href="#growth-icon" transform="translate(0, 1) scale(1.2)" />
              </g>
            </g>

          </svg>
        </div>

        {/* Brand Typography Area */}
        <div className="preloader-brand">
          <div className="brand-title">
            Smart <span className="brand-accent">Resources Qld</span>
          </div>
          <div className="brand-subtitle">
            {loadingText}
          </div>
        </div>

        {/* Progress Bar & Indicators */}
        <div className="progress-container">
          <div className="progress-track">
            <div className="progress-bar" style={{ width: `${progress}%` }}></div>
          </div>
          <div className="progress-percentage">{Math.round(progress)}%</div>
        </div>

      </div>
    </div>
  );
};

export default Preloader;
